import { Alert, Skeleton, Stack } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useFactoryAssetSets } from '../useAppFactory';
import { AppRokuCurrentImages } from './AppRokuCurrentImages';
import { AppRokuBrandKit } from './AppRokuBrandKit';
import { AppRokuManualSet } from './AppRokuManualSet';

interface AppRokuImagesPanelProps {
  appCfgId: number;
  disabled?: boolean;
}

/**
 * The Images surface orchestrator. Every card on this panel reads the
 * factory gateway, so an unreachable gateway must say so — three cards
 * each quietly returning null would leave the whole panel blank (the
 * pre-rework tab always had non-factory content to fall back on; this
 * one does not).
 */
export function AppRokuImagesPanel({ appCfgId, disabled }: AppRokuImagesPanelProps) {
  const assetSetsQuery = useFactoryAssetSets(appCfgId);

  if (assetSetsQuery.isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="orange">
        Image management is temporarily unavailable. Your app and its current images are
        unaffected — try again in a few minutes, or contact support if this persists.
      </Alert>
    );
  }
  if (assetSetsQuery.isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={120} radius="md" />
        <Skeleton height={220} radius="md" />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <AppRokuCurrentImages appCfgId={appCfgId} />
      <AppRokuBrandKit appCfgId={appCfgId} disabled={disabled} />
      <AppRokuManualSet appCfgId={appCfgId} disabled={disabled} />
    </Stack>
  );
}
